import React from 'react'
import styled from '@emotion/styled'
import { useSearchParams } from 'react-router'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { Input } from '@mobi/component-library/Selection&Input/Inputs'
import { colors, font, shadow, spacing } from '@mobi/component-library/Foundations'
import { useDisableAppHeaderSticky } from '@core/Areas/AppHeader/hooks/useDisableAppHeaderSticky'
import { ProfilesList } from '@core/Areas/BetLoop/Components/Common/ProfilesList'
import { SortProfiles } from '@core/Areas/BetLoop/Components/SearchProfiles/Components/SortProfiles'
import { useSearchProfiles } from './hooks/useSearchProfiles'
import type { ProfileSortOrder } from './types'

const enum LocalConstants {
  SearchProfilesNoResultsClass = 'js-search-profiles-no-results_label',
}

let sessionSortOrder: ProfileSortOrder = 'FollowerCount'

export const SearchProfiles = () => {
  useDisableAppHeaderSticky()

  const [searchQuery, setSearchQuery] = useSearchProfilesParamState('q', '')
  const [sortOrder, setSortOrder] = React.useState<ProfileSortOrder>(sessionSortOrder)

  const searchInputRef = React.useRef<HTMLDivElement>(null)

    React.useEffect(() => {
      sessionSortOrder = sortOrder
    }, [ sortOrder])

  React.useEffect(() => {
    const el = searchInputRef.current
    if (!el) return
    const handleScroll = () => (el.style.boxShadow = el.offsetTop > 2 ? shadow.md : 'none')
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [searchInputRef])

  const { data, LoadMoreButton, isEmptyState, isLoading, isValidSearch } = useSearchProfiles(
    searchQuery,
    sortOrder
  )

  const handleChangeSearchQuery = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchQuery(value, { shouldReplace: true })
  }

  const handleClearSearch = () => {
    setSearchQuery('', { shouldReplace: true })
    setSortOrder('FollowerCount')
  }

  const noResultsFound = (isValidSearch && isEmptyState) || /[^a-zA-Z0-9\s.,'"-]/.test(searchQuery)

  return (
    <WrapperStyled>
      <div ref={searchInputRef}>
        <div>
          <Input
            prefix={<Icon name='LineSearchSm' size='2rem' color={colors.neutral[700]} />}
            suffix={
              searchQuery && searchQuery.toString().length > 0 ? (
                <Icon onClick={handleClearSearch} name='SolidXClose' />
              ) : null
            }
            name='Search'
            type='text'
            placeholder='Search for profiles'
            value={searchQuery}
            onChange={handleChangeSearchQuery}
            data-testid='clear-search-icon'
          />
        </div>
        {!searchQuery && (
          <SortProfiles sortOrder={sortOrder} handleSortOrderChange={setSortOrder} />
        )}
      </div>
      <>
        {noResultsFound && (
          <div className={LocalConstants.SearchProfilesNoResultsClass}>
            <span>No results found for "{searchQuery}"</span>
          </div>
        )}

        <ProfilesList
          data={data}
          isLoading={isLoading}
          LoadMoreButton={LoadMoreButton}
          searchQuery={searchQuery}
          location='search-page'
          shouldShowSkeletonLoader={false}
        />
      </>
    </WrapperStyled>
  )
}

// =======
// Helpers
// =======

const useSearchProfilesParamState = (key: string, defaultValue = '') => {
  const [searchParams, setSearchParams] = useSearchParams()

  const currentValue = searchParams.get(key) ?? defaultValue

  const setValue = React.useCallback(
    (newValue: string, { shouldReplace = false }: { shouldReplace?: boolean } = {}) => {
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev)
          if (!newValue) params.delete(key)
          else params.set(key, newValue)
          return params
        },
        { replace: shouldReplace }
      )
    },
    [key, setSearchParams]
  )

  return [currentValue, setValue] as const
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'stretch',
  position: 'relative',

  '> div:first-of-type': {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    background: colors.white,
    transition: 'box-shadow 0.125s ease',

    '> div:first-of-type': {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      alignSelf: 'stretch',
      padding: `${spacing.sm} ${spacing.md}`,
    },
  },

  '> div': {
    [`&.${LocalConstants.SearchProfilesNoResultsClass}`]: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: spacing.bigx1,

      '> span': {
        fontFamily: font.family.primary,
        fontSize: font.size.lg.fontSize,
        fontWeight: font.weight.regular,
        lineHeight: font.size.lg.lineHeight,
      },
    },
  },
})
